import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { planBadgeClass, planLabel, useAuth } from "../lib/auth";
import {
  BillingError,
  postBillingCheckout,
  postBillingPortal,
  postDevPlan,
  type DevPlan,
  type Entitlements,
  type SubscriptionSummary,
  type UsageSummary,
} from "../lib/api";
import { GitHubIntegrationPanel } from "../components/GitHubIntegrationPanel";
import { LinearIntegrationPanel } from "../components/LinearIntegrationPanel";
import { JiraIntegrationPanel } from "../components/JiraIntegrationPanel";

/**
 * Account page — profile, current plan, usage meter, billing controls.
 *
 * Phase 2 surfaces:
 *   - Past-due banner (subscription.needsAttention)
 *   - Usage meter (used/included + overage count for Pro)
 *   - Upgrade CTA → /api/billing/checkout → Stripe hosted Checkout
 *   - Manage billing → /api/billing/portal → Stripe hosted Portal
 */
export function AccountPage(): JSX.Element {
  const {
    user,
    entitlements,
    subscription,
    usage,
    ready,
    disabled,
    devPlanOverrideEnabled,
    signOut,
    refresh,
  } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);

  // Bounce anonymous users to sign-in. We don't show a 404 — the account
  // page simply has no meaning without a user.
  useEffect(() => {
    if (ready && !user && !disabled) {
      navigate(`/sign-in?redirect=${encodeURIComponent("/account")}`, { replace: true });
    }
  }, [ready, user, disabled, navigate]);

  // After Stripe's Checkout redirects back with ?checkout=success we refresh
  // /api/auth/me so the UI flips from "Upgrade" → "Manage billing" without
  // needing a hard reload. Matches STRIPE_CHECKOUT_SUCCESS_URL's default.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      refresh().catch(() => undefined);
    }
  }, [refresh]);

  if (!ready) {
    return <div className="text-sm text-slate-400">Loading account…</div>;
  }
  if (!user) {
    return <div className="text-sm text-slate-400">Redirecting…</div>;
  }

  async function handleSignOut(): Promise<void> {
    setSigningOut(true);
    try {
      await signOut();
      navigate("/", { replace: true });
    } finally {
      setSigningOut(false);
    }
  }

  async function handleUpgrade(
    tier: "starter" | "pro" | "team",
    seats?: number,
  ): Promise<void> {
    setBillingError(null);
    setBillingBusy(true);
    try {
      const res = await postBillingCheckout(tier, seats);
      window.location.assign(res.url);
    } catch (err) {
      setBillingBusy(false);
      setBillingError(billingErrorMessage(err));
    }
  }

  async function handlePortal(): Promise<void> {
    setBillingError(null);
    setBillingBusy(true);
    try {
      const res = await postBillingPortal();
      window.location.assign(res.url);
    } catch (err) {
      setBillingBusy(false);
      setBillingError(billingErrorMessage(err));
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Account</h1>
          <p className="mt-1 text-sm text-slate-400">
            Your profile, usage, and billing. Usage resets on the 1st of each
            month (UTC).
          </p>
        </div>
        <Link to="/" className="text-sm text-slate-400 hover:text-white">
          ← back to dashboard
        </Link>
      </header>

      {subscription?.needsAttention && (
        <PastDueBanner
          subscription={subscription}
          onManage={handlePortal}
          busy={billingBusy}
        />
      )}

      {/* ── profile card ────────────────────────────────────────────── */}
      <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
        <div className="flex items-start gap-4">
          <Avatar name={user.name ?? user.email} src={user.avatarUrl} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3">
              <div className="truncate text-lg font-medium text-slate-100">
                {user.name ?? user.email}
              </div>
              <span className={`rounded-full px-2.5 py-0.5 text-xs ${planBadgeClass(entitlements.plan)}`}>
                {planLabel(entitlements.plan)}
              </span>
            </div>
            <div className="mt-0.5 truncate text-sm text-slate-400">{user.email}</div>
            <div className="mt-1 text-xs text-slate-500">
              Signed in with{" "}
              <span className="text-slate-300">
                {user.authProvider === "google"
                  ? "Google"
                  : user.authProvider === "github"
                    ? "GitHub"
                    : "local"}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            disabled={signingOut}
            className="rounded-md border border-slate-700 px-3 py-2 text-sm text-slate-300 hover:bg-slate-800 disabled:opacity-50"
          >
            {signingOut ? "Signing out…" : "Sign out"}
          </button>
        </div>
      </section>

      {/* ── usage meter ─────────────────────────────────────────────── */}
      {usage && (
        <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
          <header className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-medium">Usage this month</h2>
            <span className="text-xs text-slate-500">{usage.bucketMonth} (UTC)</span>
          </header>
          <UsageMeter usage={usage} plan={entitlements.plan} />
        </section>
      )}

      {/* ── plan / billing ──────────────────────────────────────────── */}
      <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-medium">Plan</h2>
          <BillingCta
            plan={entitlements.plan}
            hasActiveSub={Boolean(subscription?.isActive)}
            hasStripeCustomer={Boolean(subscription)}
            onUpgrade={handleUpgrade}
            onPortal={handlePortal}
            busy={billingBusy}
          />
        </header>
        {billingError && (
          <div className="mb-4 rounded-md border border-rose-800/60 bg-rose-950/50 px-3 py-2 text-xs text-rose-200">
            {billingError}
          </div>
        )}
        {subscription && <SubscriptionDetails subscription={subscription} />}
        <EntitlementGrid entitlements={entitlements} />
        <PlanFootnote plan={entitlements.plan} />
      </section>

      {/* ── GitHub integration (Phase 1) ────────────────────────────── */}
      <GitHubIntegrationPanel />

      {/*
        Linear + Jira are Pro+ features (Plan §21 Phase 4). We hide the
        connect panel entirely when the user's plan doesn't include the
        channel — the server still returns 402 INTEGRATION_NOT_ALLOWED
        defensively. Showing a locked preview would also be reasonable;
        we chose hide-when-locked to keep the Account page tidy for the
        long Free + Starter tail. The "Notifications" Stat row in
        EntitlementGrid above already advertises which channels each
        tier unlocks.
      */}
      {entitlements.integrations?.includes("linear") && (
        <LinearIntegrationPanel />
      )}
      {entitlements.integrations?.includes("jira") && (
        <JiraIntegrationPanel />
      )}

      {/* ── Dev-only plan switcher (UAIP_DEV_PLAN_OVERRIDE=1) ───────── */}
      {devPlanOverrideEnabled && (
        <DevPlanPanel currentPlan={entitlements.plan} onSwitched={() => refresh()} />
      )}
    </div>
  );
}

function billingErrorMessage(err: unknown): string {
  if (err instanceof BillingError) {
    switch (err.code) {
      case "BILLING_DISABLED":
        return "Billing isn't configured on this deployment. Contact your admin to enable it.";
      case "AUTH_REQUIRED":
        return "Please sign in again and retry.";
      case "ALREADY_SUBSCRIBED":
        return "You already have an active Pro subscription. Use 'Manage billing' instead.";
      case "NO_STRIPE_CUSTOMER":
        return "No billing record yet — upgrade first to open the portal.";
      case "BILLING_CONFIG_INCOMPLETE":
        return "Billing redirect URLs aren't configured. Contact your admin.";
      case "STRIPE_UPSTREAM_ERROR":
      default:
        return `Stripe is unreachable right now (${err.message}). Try again in a moment.`;
    }
  }
  return "Something went wrong. Try again in a moment.";
}

// ────────────────────────── sub-components ──────────────────────────

function Avatar(props: { name: string; src: string | null }): JSX.Element {
  if (props.src) {
    return (
      <img
        src={props.src}
        alt=""
        className="h-14 w-14 rounded-full border border-slate-700 object-cover"
      />
    );
  }
  const initial = props.name.trim().charAt(0).toUpperCase() || "?";
  return (
    <div className="grid h-14 w-14 place-items-center rounded-full border border-slate-700 bg-slate-800 text-lg font-medium text-slate-100">
      {initial}
    </div>
  );
}

function BillingCta(props: {
  plan: Entitlements["plan"];
  hasActiveSub: boolean;
  hasStripeCustomer: boolean;
  onUpgrade: (tier: "starter" | "pro" | "team", seats?: number) => void;
  onPortal: () => void;
  busy: boolean;
}): JSX.Element | null {
  // Active Team customer → portal-only (seats are managed there).
  if (props.hasStripeCustomer && props.hasActiveSub && props.plan === "team") {
    return (
      <button
        type="button"
        onClick={props.onPortal}
        disabled={props.busy}
        className="rounded-md border border-slate-700 px-3 py-1.5 text-sm font-medium text-slate-200 hover:bg-slate-800 disabled:opacity-50"
      >
        {props.busy ? "Opening…" : "Manage billing & seats"}
      </button>
    );
  }

  // Active Pro customer → portal + cross-grade-to-Team option.
  if (props.hasStripeCustomer && props.hasActiveSub && props.plan === "pro") {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <UpgradeToTeamButton />
        <button
          type="button"
          onClick={props.onPortal}
          disabled={props.busy}
          className="rounded-md border border-slate-700 px-3 py-1.5 text-sm font-medium text-slate-200 hover:bg-slate-800 disabled:opacity-50"
        >
          {props.busy ? "Opening…" : "Manage billing"}
        </button>
      </div>
    );
  }

  // Active Starter user → offer Pro upgrade + Team upgrade + Manage billing.
  if (props.hasStripeCustomer && props.hasActiveSub && props.plan === "starter") {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => props.onUpgrade("pro")}
          disabled={props.busy}
          className="rounded-md bg-indigo-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50"
        >
          {props.busy ? "Redirecting…" : "Upgrade to Pro"}
        </button>
        <UpgradeToTeamButton />
        <button
          type="button"
          onClick={props.onPortal}
          disabled={props.busy}
          className="rounded-md border border-slate-700 px-3 py-1.5 text-sm font-medium text-slate-200 hover:bg-slate-800 disabled:opacity-50"
        >
          Manage billing
        </button>
      </div>
    );
  }

  // Anyone who isn't already on an active paid plan sees three upgrade CTAs.
  if (props.plan === "free" || props.plan === "anonymous" || !props.hasActiveSub) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => props.onUpgrade("starter")}
          disabled={props.busy}
          className="rounded-md border border-sky-700/70 bg-sky-900/40 px-3 py-1.5 text-sm font-medium text-sky-100 hover:bg-sky-900/70 disabled:opacity-50"
        >
          {props.busy ? "…" : "Upgrade to Starter"}
        </button>
        <button
          type="button"
          onClick={() => props.onUpgrade("pro")}
          disabled={props.busy}
          className="rounded-md bg-indigo-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-400 disabled:opacity-50"
        >
          {props.busy ? "Redirecting…" : "Upgrade to Pro"}
        </button>
        <UpgradeToTeamButton />
      </div>
    );
  }
  return null;
}

/**
 * Team / Enterprise is sales-led — no self-serve checkout and no public price.
 * Renders a "Contact Us" link to sales. Existing Team customers still manage
 * their seats via the billing portal (see the active-Team branch in BillingCta).
 */
function UpgradeToTeamButton(): JSX.Element {
  return (
    <a
      href="mailto:sales@uaip.dev?subject=UAIP%20Team%20%2F%20Enterprise%20enquiry"
      className="rounded-md border border-emerald-700/70 bg-emerald-900/40 px-3 py-1.5 text-sm font-medium text-emerald-100 hover:bg-emerald-900/70"
    >
      Contact Us for Team
    </a>
  );
}

function PastDueBanner(props: {
  subscription: SubscriptionSummary;
  onManage: () => void;
  busy: boolean;
}): JSX.Element {
  const reason =
    props.subscription.status === "past_due"
      ? "A recent payment failed."
      : props.subscription.status === "unpaid"
        ? "Your subscription has unpaid invoices."
        : "We couldn't collect your last payment.";
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-amber-700/60 bg-amber-950/40 px-5 py-4">
      <div>
        <div className="text-sm font-medium text-amber-200">
          Your billing needs attention
        </div>
        <p className="mt-0.5 text-xs text-amber-200/80">
          {reason} Update your payment method to keep Pro access. We retain
          your access during a short grace period.
        </p>
      </div>
      <button
        type="button"
        onClick={props.onManage}
        disabled={props.busy}
        className="shrink-0 rounded-md bg-amber-500 px-3 py-2 text-xs font-medium text-amber-950 hover:bg-amber-400 disabled:opacity-50"
      >
        {props.busy ? "Opening…" : "Fix payment"}
      </button>
    </div>
  );
}

function UsageMeter({
  usage,
  plan,
}: {
  usage: UsageSummary;
  plan: Entitlements["plan"];
}): JSX.Element {
  const pct = Math.min(100, usage.percentOfIncluded);
  const isOverage = usage.overage > 0;
  const barColour = isOverage
    ? "bg-indigo-400"
    : pct >= 90
      ? "bg-amber-400"
      : pct >= 75
        ? "bg-amber-500/80"
        : "bg-indigo-500";
  return (
    <>
      <div className="mb-1 flex items-end justify-between text-sm">
        <div>
          <span className="text-slate-100">{usage.used}</span>
          <span className="text-slate-400"> / {usage.included} included</span>
          {isOverage && (
            <span className="ml-2 rounded-full bg-indigo-900/60 px-2 py-0.5 text-xs text-indigo-200">
              +{usage.overage} extra (billed)
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500">
          {usage.percentOfIncluded}% of included
        </div>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-800">
        <div
          className={`h-full ${barColour} transition-[width]`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <p className="mt-3 text-xs text-slate-500">
        {plan === "pro"
          ? "Pro includes 500 crawls/month. Additional crawls are billed via Stripe metered overage."
          : plan === "starter"
            ? `${Math.max(0, usage.included - usage.used)} crawls remaining this month on the Starter plan.`
            : plan === "free"
              ? `${Math.max(0, usage.included - usage.used)} crawls remaining this month on the Free plan.`
              : "Usage shown for transparency."}
      </p>
    </>
  );
}

function SubscriptionDetails({
  subscription,
}: {
  subscription: SubscriptionSummary;
}): JSX.Element {
  return (
    <div className="mb-4 rounded-md border border-slate-800 bg-slate-950/50 px-3 py-2.5 text-xs text-slate-400">
      <div className="flex items-center gap-2">
        <span className="text-slate-500">Status:</span>
        <StatusPill status={subscription.status} />
        {subscription.currentPeriodEnd && (
          <span className="text-slate-500">
            · renews{" "}
            <span className="text-slate-300">
              {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string | null }): JSX.Element {
  const label = status ?? "inactive";
  const cls =
    status === "active" || status === "trialing"
      ? "bg-emerald-900/40 text-emerald-200 border-emerald-700/60"
      : status === "past_due" || status === "unpaid"
        ? "bg-amber-900/40 text-amber-200 border-amber-700/60"
        : status === "canceled"
          ? "bg-slate-800 text-slate-300 border-slate-700"
          : "bg-slate-800 text-slate-300 border-slate-700";
  return (
    <span className={`rounded-full border px-2 py-0.5 text-xs ${cls}`}>
      {label}
    </span>
  );
}

function EntitlementGrid({ entitlements }: { entitlements: Entitlements }): JSX.Element {
  // A "0" in the schema means "unlimited" for several fields — translate
  // that here so users don't see a confusing "0".
  const formatLimit = (n: number, unit: string): string =>
    n === 0 ? "unlimited" : `${n} ${unit}`;

  // For anonymous / free we have different quota axes (day vs month).
  const quotaRow =
    entitlements.plan === "anonymous"
      ? { label: "Crawls", value: formatLimit(entitlements.crawlsPerDay, "/day") }
      : {
          label: "Crawls",
          value:
            entitlements.crawlsPerMonth > 0
              ? `${entitlements.crawlsPerMonth}/month${entitlements.plan === "pro" ? " included" : ""}`
              : "unlimited",
        };

  // Each plan's "projects" cap — shown in its own row so users can see the
  // container-vs-URL distinction clearly (a project is a named workspace
  // that can host many crawls against many URLs).
  const projectsValue = (() => {
    switch (entitlements.plan) {
      case "anonymous":
        return "sign in required";
      case "free":
        return "1 project";
      case "starter":
        return "3 projects";
      case "pro":
        return "10 projects";
      case "team":
        return "25 projects";
      case "enterprise":
      case "admin":
        return "unlimited";
    }
  })();

  const emitValue = (() => {
    if (entitlements.allowedEmits.includes("*")) return "all 7 frameworks";
    const n = entitlements.allowedEmits.length;
    return n === 1 ? "Playwright TS only" : `${n} frameworks`;
  })();

  const schedulingValue =
    entitlements.plan === "pro" ||
    entitlements.plan === "team" ||
    entitlements.plan === "enterprise" ||
    entitlements.plan === "admin"
      ? "any cron"
      : entitlements.plan === "starter"
        ? "daily"
        : "not included";

  const integrationsValue = (() => {
    switch (entitlements.plan) {
      case "free":
      case "anonymous":
        return "community";
      case "starter":
        return "Slack";
      case "pro":
        return "Slack + Jira + Linear";
      case "team":
      case "enterprise":
      case "admin":
        return "Slack + Jira + Linear";
    }
  })();

  return (
    <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <Stat label={quotaRow.label} value={quotaRow.value} />
      <Stat
        label="Pages per crawl"
        value={formatLimit(entitlements.maxPagesPerCrawl, "pages")}
      />
      <Stat label="Projects" value={projectsValue} />
      <Stat label="Emit frameworks" value={emitValue} />
      <Stat label="Scheduling" value={schedulingValue} />
      <Stat label="Notifications" value={integrationsValue} />
      {/* Only surfaced when the Ollama-backed LLM feature is enabled for the
          deployment (UAIP_ENABLE_SEMANTIC); hidden otherwise. */}
      {entitlements.allowSemantic && (
        <Stat label="Semantic / LLM triage" value="included" />
      )}
      <Stat
        label="Self-heal"
        value={
          entitlements.plan === "pro" ||
          entitlements.plan === "team" ||
          entitlements.plan === "enterprise" ||
          entitlements.plan === "admin"
            ? "included"
            : "Pro only"
        }
      />
    </dl>
  );
}

function Stat(props: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-md border border-slate-800 bg-slate-950/50 px-3 py-2">
      <dt className="text-xs uppercase tracking-wider text-slate-500">
        {props.label}
      </dt>
      <dd className="mt-0.5 text-sm text-slate-100">{props.value}</dd>
    </div>
  );
}

function PlanFootnote({ plan }: { plan: Entitlements["plan"] }): JSX.Element | null {
  if (plan === "free") {
    return (
      <p className="mt-4 text-xs text-slate-500">
        Free includes 5 crawls/month, 1 project, 25 URLs per crawl, and the
        Playwright TS emitter. Upgrade to <span className="text-slate-300">Starter</span> for
        3 projects, 250 URLs/crawl, Selenium + Playwright (Java/Python), daily
        schedules, and Slack alerts — or jump to{" "}
        <span className="text-slate-300">Pro</span> for 10 projects, 2000
        URLs/crawl, all 7 emitters (adds Cypress + UiPath), arbitrary cron,
        self-heal, and Slack + Jira.
      </p>
    );
  }
  if (plan === "starter") {
    return (
      <p className="mt-4 text-xs text-slate-500">
        Starter includes 100 crawls/month, 3 projects, 250 URLs per crawl,
        Playwright TS + Selenium (Java/Python) + Playwright (Java/Python),
        daily schedules, and Slack alerts. Upgrade to{" "}
        <span className="text-slate-300">Pro</span> for 500 crawls/month,
        10 projects, 2000 URLs/crawl, all 7 emitters (adds Cypress + UiPath),
        arbitrary cron, self-heal, and Slack + Jira.
      </p>
    );
  }
  if (plan === "pro") {
    return (
      <p className="mt-4 text-xs text-slate-500">
        Pro includes 500 crawls/month. Additional crawls are billed at $0.50
        each via Stripe metered billing and invoiced at the end of the period.
        Includes all 7 emitters, arbitrary cron, self-heal, and
        Slack + Jira integrations.
      </p>
    );
  }
  if (plan === "enterprise" || plan === "admin") {
    return (
      <p className="mt-4 text-xs text-slate-500">
        Enterprise tier — quotas waived. Contact{" "}
        <a className="text-slate-300 hover:text-white" href="mailto:sales@uaip.dev">
          sales@uaip.dev
        </a>{" "}
        for SLA + SSO changes.
      </p>
    );
  }
  return null;
}

// ────────────────────────── Dev plan switcher ──────────────────────────
/**
 * Only rendered when the server advertises `devPlanOverrideEnabled`.
 * Lets a developer flip their own tier on the fly to exercise gate code
 * paths without buying/canceling real Stripe subscriptions. See
 * apps/api/src/dev/routes.ts for the matching endpoint.
 */
function DevPlanPanel(props: {
  currentPlan: Entitlements["plan"];
  onSwitched: () => void | Promise<void>;
}): JSX.Element {
  const [busy, setBusy] = useState<DevPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const choices: { plan: DevPlan; label: string; blurb: string }[] = [
    { plan: "free", label: "Free", blurb: "5 crawls/mo · 1 project · Playwright TS only" },
    { plan: "starter", label: "Starter", blurb: "100 crawls/mo · 3 projects · + Selenium + Playwright Java/Python" },
    { plan: "pro", label: "Pro", blurb: "500 crawls/mo · 10 projects · all 7 emitters · self-heal" },
    { plan: "team", label: "Team", blurb: "2,000 crawls/mo · 25 projects · Pro + assignees + comments" },
    { plan: "enterprise", label: "Enterprise", blurb: "quotas waived" },
  ];

  async function apply(plan: DevPlan): Promise<void> {
    setBusy(plan);
    setError(null);
    try {
      await postDevPlan(plan);
      await props.onSwitched();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to switch plan");
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="rounded-xl border border-amber-700/50 bg-amber-950/20 p-5">
      <header className="mb-2 flex items-center justify-between">
        <h2 className="text-lg font-medium text-amber-100">Dev: switch plan</h2>
        <span className="rounded-full border border-amber-700/60 bg-amber-900/40 px-2 py-0.5 text-[11px] uppercase tracking-wider text-amber-200">
          UAIP_DEV_PLAN_OVERRIDE
        </span>
      </header>
      <p className="mb-4 text-xs text-amber-200/80">
        Flips your <span className="font-mono">users.plan</span> directly — no
        Stripe round-trip. The matching endpoint is only registered when the
        server env var is set. Never ships in production.
      </p>
      {error && (
        <div className="mb-3 rounded-md border border-rose-700/60 bg-rose-950/40 px-3 py-2 text-xs text-rose-200">
          {error}
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-2">
        {choices.map((c) => {
          const active = props.currentPlan === c.plan;
          return (
            <button
              key={c.plan}
              type="button"
              onClick={() => apply(c.plan)}
              disabled={busy !== null}
              className={`rounded-lg border px-3 py-2 text-left transition ${
                active
                  ? "border-amber-500/70 bg-amber-900/50"
                  : "border-slate-700 bg-slate-900/60 hover:border-amber-600/60 hover:bg-amber-950/30"
              } disabled:opacity-60`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-100">{c.label}</span>
                {active && (
                  <span className="text-[10px] uppercase tracking-wider text-amber-200">
                    current
                  </span>
                )}
                {busy === c.plan && (
                  <span className="text-[10px] text-amber-200">switching…</span>
                )}
              </div>
              <div className="mt-1 text-xs text-slate-400">{c.blurb}</div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
